import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import p5 from 'p5';
import { createErrorHandlingWrapper, setupAssetPathRedirection } from '@/components/EditorUtils';

/**
 * Composant qui affiche uniquement le canvas d'un jeu p5.js au centre de l'écran
 * @param {Object} props - Propriétés du composant
 * @param {string} props.code - Code source du jeu
 * @param {string} props.workdir - Répertoire pour rediriger les chemins d'assets
 * @param {Function} props.onError - Fonction appelée en cas d'erreur
 * @param {Object} ref - Référence pour exposer des méthodes
 */
const CanvasPlay = forwardRef(({ code, workdir, onError }, ref) => {
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const sketchInstance = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);

  // Exposer les méthodes à travers la ref
  useImperativeHandle(ref, () => ({
    runSketch: () => runSketch(),
    cleanupSketch: () => cleanupSketch()
  }));

  // Nettoyer le sketch à la destruction du composant
  useEffect(() => {
    return () => {
      cleanupSketch();
    };
  }, []);


  // Nettoyer le sketch courant
  const cleanupSketch = () => {
    if (sketchInstance.current) {
      try {
        sketchInstance.current.remove();
      } catch (e) {
        console.error("Erreur lors du nettoyage du sketch:", e);
      }
      sketchInstance.current = null;

      if (canvasRef.current) {
        while (canvasRef.current.firstChild) {
          canvasRef.current.removeChild(canvasRef.current.firstChild);
        }
      }
    }
  };

  // Fonction pour afficher un message d'erreur dans le canvas
  const showErrorInCanvas = (errorLine, errorMessage) => {
    setErrorMessage(errorLine ? `Erreur ligne ${errorLine}: ${errorMessage}` : `Erreur: ${errorMessage}`);
    
    if (onError) {
      onError({
        lineNumber: errorLine,
        message: errorMessage
      });
    }

    // Nettoyage des canvas existants
    cleanupSketch();
    
    // Créer un nouveau canvas avec le message d'erreur
    setTimeout(() => {
      const errorSketch = (p) => {
        p.setup = function() {
          p.createCanvas(600, 600);
          p.background(50);
          p.fill(255);
          p.textSize(16);
          p.textAlign(p.CENTER, p.CENTER);
          p.text(errorMessage, p.width/2, p.height/2);
        }
        p.draw = function() {} // Fonction vide
        p.noLoop(); // Important: arrêter la boucle draw
      };
      
      sketchInstance.current = new p5(errorSketch, canvasRef.current);
    }, 0);
  };

  // Fonction pour exécuter le sketch
  const runSketch = () => {
    cleanupSketch();
    setErrorMessage(null);

    const sketchFn = (p) => {
      try {
        const wrappedCode = `${code}\n//# sourceURL=userCode.js`;

        // Définir les fonctions utilisateur à partir du code
        const userFn = new Function('p', `
          with(p) {
            ${wrappedCode}
            return {
              preload: typeof preload === 'function' ? preload : null,
              setup: typeof setup === 'function' ? setup : null,
              draw: typeof draw === 'function' ? draw : null,
              mousePressed: typeof mousePressed === 'function' ? mousePressed : null,
              mouseReleased: typeof mouseReleased === 'function' ? mouseReleased : null,
              mouseMoved: typeof mouseMoved === 'function' ? mouseMoved : null,
              keyPressed: typeof keyPressed === 'function' ? keyPressed : null,
              keyReleased: typeof keyReleased === 'function' ? keyReleased : null
            };
          }
        `);

        const userFunctions = userFn(p);

        // Callback d'erreur pour les wrappers
        const errorCallback = (errorLine, errorMessage, stack) => {
          showErrorInCanvas(errorLine, errorMessage);
        };

        // Configurer la redirection des chemins d'assets
        if (workdir) {
          setupAssetPathRedirection(p, workdir, errorCallback);
        }

        // Ajouter le support pour preload
        if (userFunctions.preload) {
          p.preload = createErrorHandlingWrapper(
            function() {
              userFunctions.preload.call(p);
            },
            "preload",
            errorCallback,
            false
          );
        }

        // Appliquer les wrappers aux fonctions p5.js
        p.setup = createErrorHandlingWrapper(
          function() {
            // Setup par défaut si l'utilisateur n'en a pas défini
            if (!userFunctions.setup) {
              p.createCanvas(600, 600);
              p.background(220);
            } else {
              userFunctions.setup.call(p);
            }
          }, 
          "setup",
          errorCallback,
          false
        );

        p.draw = createErrorHandlingWrapper(
          function() {
            if (userFunctions.draw) {
              userFunctions.draw.call(p);
            }
          }, 
          "draw", 
          errorCallback,
          true
        );

        // Appliquer les wrappers aux autres fonctions d'événements
        if (userFunctions.mousePressed) {
          p.mousePressed = createErrorHandlingWrapper(
            () => userFunctions.mousePressed.call(p),
            "mousePressed",
            errorCallback
          );
        }
        
        if (userFunctions.mouseReleased) {
          p.mouseReleased = createErrorHandlingWrapper(
            () => userFunctions.mouseReleased.call(p),
            "mouseReleased",
            errorCallback
          );
        }
        
        if (userFunctions.mouseMoved) {
          p.mouseMoved = createErrorHandlingWrapper(
            () => userFunctions.mouseMoved.call(p),
            "mouseMoved",
            errorCallback
          );
        }
        
        if (userFunctions.keyPressed) {
          p.keyPressed = createErrorHandlingWrapper(
            () => userFunctions.keyPressed.call(p),
            "keyPressed",
            errorCallback
          );
        }
        
        if (userFunctions.keyReleased) {
          p.keyReleased = createErrorHandlingWrapper(
            () => userFunctions.keyReleased.call(p),
            "keyReleased",
            errorCallback
          );
        }

      } catch (error) {
        console.error("Erreur lors de l'exécution du code:", error);
        showErrorInCanvas(null, error.message);
        return false;
      }
    };

    setTimeout(() => {
      sketchInstance.current = new p5(sketchFn, canvasRef.current);
    }, 0);
  };

  // Centrer et redimensionner le canvas dans son conteneur
  useEffect(() => {
    const centerAndScaleCanvas = () => {
      if (canvasRef.current && canvasContainerRef.current) {
        const canvases = canvasRef.current.querySelectorAll('canvas');
        if (canvases.length > 0) {
          const canvas = canvases[0];
          const container = canvasContainerRef.current;
          
          // Obtenir les dimensions originales du canvas
          const originalWidth = canvas.width;
          const originalHeight = canvas.height;
          
          if (originalWidth === 0 || originalHeight === 0) return; // Éviter la division par zéro
          
          // Obtenir les dimensions disponibles du conteneur
          const containerRect = container.getBoundingClientRect();
          
          // Sur mobile, utiliser la largeur de la fenêtre moins les marges
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Calculer l'espace disponible en tenant compte des marges et padding
          const availableWidth = Math.min(containerRect.width - 32, viewportWidth - 32);
          const maxHeight = viewportHeight * 0.6; // Maximum 60% de la hauteur de l'écran
          
          // Calculer le ratio de redimensionnement pour préserver les proportions
          const widthRatio = availableWidth / originalWidth;
          const heightRatio = maxHeight / originalHeight;
          const scale = Math.min(widthRatio, heightRatio, 1); // Ne pas agrandir, seulement réduire
          
          // Appliquer le redimensionnement
          const newWidth = Math.floor(originalWidth * scale);
          const newHeight = Math.floor(originalHeight * scale);
          
          canvas.style.display = 'block';
          canvas.style.margin = 'auto';
          canvas.style.width = `${newWidth}px`;
          canvas.style.height = `${newHeight}px`;
          canvas.style.maxWidth = '100%';
          canvas.style.maxHeight = '100%';
          canvas.style.borderRadius = '16px';
          canvas.style.overflow = 'hidden';
          canvas.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.12)';
        }
      }
    };

    // Observer le conteneur pour détecter quand un canvas est ajouté
    const observer = new MutationObserver(centerAndScaleCanvas);
    
    if (canvasRef.current) {
      observer.observe(canvasRef.current, { childList: true, subtree: true });
    }

    // Appliquer le centrage et redimensionnement immédiatement si un canvas existe déjà
    centerAndScaleCanvas();
    
    // Écouter les changements de taille de la fenêtre pour redimensionner
    const handleResize = () => {
      setTimeout(centerAndScaleCanvas, 100); // Petit délai pour laisser le temps au navigateur de recalculer
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="w-full flex items-center justify-center p-4 min-h-0" ref={canvasContainerRef}>
      <div 
        ref={canvasRef} 
        style={{ 
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}
      />
      {errorMessage && (
        <div className="absolute bottom-4 left-0 right-0 mx-auto w-full max-w-md bg-red-500 text-white p-4 rounded-md text-center">
          {errorMessage}
        </div>
      )}
    </div>
  );
});

CanvasPlay.displayName = 'CanvasPlay';

export default CanvasPlay; 